import { useEffect, useState, type ReactElement } from 'react';

import type { StreamState, TraceEntry } from '@harness/stream-core';

/**
 * The strongest thing to demo. Fetches `GET /v1/streams/:id/trace`, server
 * truth, and diffs it against the client's own timeline: every frame the
 * server sent that the client never applied, and why, read straight off the
 * client's own markers rather than guessed at.
 */

interface ServerTraceResponse {
  streamId: string;
  complete: boolean;
  oldestRetainedId: number | null;
  newestId: number | null;
  evictedCount: number;
  frames: { id: number; event: string; bytes: number }[];
}

type FrameLike = Extract<TraceEntry, { kind: 'frame' }>;

interface DroppedFrame {
  id: number;
  event: string;
  reason: string;
}

function explainDrop(id: number, state: StreamState): string {
  const entry = state.timeline.find(
    (item) => (item.kind === 'duplicate' || item.kind === 'stale') && item.id === id,
  );
  if (entry?.kind === 'duplicate') return 'seen twice, applied once';
  if (entry?.kind === 'stale') return 'arrived stale, discarded';
  const inGap = state.timeline.some(
    (item) => item.kind === 'gap' && id > item.fromId && id < item.toId,
  );
  if (inGap) return 'inside a marked gap';
  return 'never reached this client';
}

export function TraceComparison({
  streamId,
  clientState,
}: {
  streamId: string;
  clientState: StreamState;
}): ReactElement {
  const [trace, setTrace] = useState<ServerTraceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/v1/streams/${streamId}/trace`)
      .then(async (response) => {
        if (!response.ok) throw new Error(`the server answered ${String(response.status)}`);
        setTrace((await response.json()) as ServerTraceResponse);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'the request failed');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [streamId]);

  if (loading) return <p className="muted inspector__trace-status">Fetching server trace.</p>;
  if (error !== null) {
    return <p className="muted inspector__trace-status">Could not compare: {error}</p>;
  }
  if (trace === null) return <></>;

  const clientIds = new Set(
    clientState.timeline
      .filter((entry): entry is FrameLike => entry.kind === 'frame')
      .map((entry) => entry.id)
      .filter((id): id is number => id !== null),
  );

  const dropped: DroppedFrame[] = trace.frames
    .filter((frame) => !clientIds.has(frame.id))
    .map((frame) => ({
      id: frame.id,
      event: frame.event,
      reason: explainDrop(frame.id, clientState),
    }));

  return (
    <div className="inspector__trace">
      <div className="kv">
        <span className="kv__key">server frames</span>
        <span className="kv__value">{trace.frames.length}</span>
      </div>
      <div className="kv">
        <span className="kv__key">client applied</span>
        <span className="kv__value">{clientIds.size}</span>
      </div>
      <div className={`kv${dropped.length === 0 ? ' kv--last' : ''}`}>
        <span className="kv__key">evicted server side</span>
        <span className="kv__value">{trace.evictedCount}</span>
      </div>
      {dropped.length === 0 ? (
        <p className="muted inspector__trace-status">
          Every frame the server sent, the client applied.
        </p>
      ) : (
        <>
          <p className="muted inspector__trace-status">
            <span className="mono">{dropped.length}</span> frame
            {dropped.length === 1 ? '' : 's'} the server sent never landed on the client:
          </p>
          <ul className="inspector__frames" tabIndex={0} aria-label="Frames dropped by the client">
            {dropped.map((frame) => (
              <li key={frame.id} className="mono inspector__frame">
                #{frame.id} {frame.event}: {frame.reason}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
